import ProductImageDecoratedAdornment from '@sprinx/react-com/ProductImageDecoratedAdornment';
import ProductImageDecoratedAdornmentPercent from '@sprinx/react-com/ProductImageDecoratedAdornmentPercent';
import { TranslationDictionary } from '@sprinx/react-globalize/types';
import cond from 'lodash/fp/cond';
import React from 'react';
import { ProductAdornmentDefinition, ProductAdornmentDefinitionMapStrategy } from '../../@sprinx/knihovka-types';
import ProductImageDecoratedAdornmentPharmakon from '../../components/ProductImageDecoratedAdornmentPharmakon';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';

/**
 * Configuration of map from `view` to adornments.
 *
 * Please feel free to add custom views and mappers.
 */
const adornmentMap = transform({
  cardBig: {
    adornmentAsideLeft: [],
    adornmentAsideRight: [],
    adornmentInsideBottomLeft: [],
    adornmentInsideBottomRight: [],
    adornmentInsideTopLeft: [
      ['pharmakon', ProductImageDecoratedAdornmentPharmakon, {}],
      ['gift', null, { bgColor: '#9575cd' }],
      'sale',
    ],
    adornmentInsideTopRight: [['discount', ProductImageDecoratedAdornmentPercent, { bgColor: 'red', size: 'normal' }]],
  },
  card: {
    adornmentAsideLeft: [],
    adornmentAsideRight: [],
    adornmentInsideBottomLeft: [],
    adornmentInsideBottomRight: [],
    adornmentInsideTopLeft: [
      ['pharmakon', ProductImageDecoratedAdornmentPharmakon, {}],
      ['gift', null, { bgColor: '#9575cd' }],
      'sale',
    ],
    adornmentInsideTopRight: [['discount', ProductImageDecoratedAdornmentPercent, { bgColor: 'red', size: 'normal' }]],
  },
  row: {
    adornmentAsideLeft: [],
    adornmentAsideRight: [],
    adornmentInsideBottomLeft: [],
    adornmentInsideBottomRight: [],
    adornmentInsideTopLeft: [
      ['pharmakon', ProductImageDecoratedAdornmentPharmakon, {}],
      ['gift', null, { bgColor: '#9575cd' }],
      'sale',
    ],
    adornmentInsideTopRight: [['discount', ProductImageDecoratedAdornmentPercent, { bgColor: 'red', size: 'normal' }]],
  },
  detail: {
    adornmentAsideLeft: [],
    adornmentAsideRight: [],
    adornmentInsideBottomLeft: [],
    adornmentInsideBottomRight: [],
    adornmentInsideTopLeft: [
      ['pharmakon', ProductImageDecoratedAdornmentPharmakon, {}],
      ['gift', null, { bgColor: '#9575cd' }],
      'sale',
    ],
    adornmentInsideTopRight: [['discount', ProductImageDecoratedAdornmentPercent, { bgColor: 'red', size: 'normal' }]],
  },
});

/**
 * Returns adornemt component representation per position in specified `view`.
 *
 * @param {*} view
 * @param {*} productAdornments
 * @param {*} product
 */
export default function useProductAdornmentsMapper<P extends { taxonomies?: string[] }>(
  view: keyof typeof adornmentMap,
  productAdornments: ProductAdornmentDefinition[] | null | undefined,
  product: P,
): {
  [adornmentPosition: string]: JSX.Element[];
} {
  const t = useTranslateWithFallback();

  return React.useMemo(() => {
    const viewMap = adornmentMap[view];
    if (!viewMap) return {};

    const extendedAdornments =
      product.taxonomies &&
      product.taxonomies.includes('/pharmakon') &&
      !productAdornments?.some((a) => a.type === 'pharmakon')
        ? [...(productAdornments || []), { type: 'pharmakon', content: 'pharmakon' }]
        : productAdornments || [];

    return (extendedAdornments || []).reduce((r, { content, type }) => {
      const cfg = viewMap[type];
      if (!cfg) return r;
      const [adornmentPosition, Component, props] = cfg;

      // content: string | Object | TranslationDictionary | MapStrategy

      const [children, moreProps] = cond([
        // simple string content
        [(c) => typeof c === 'string', (c) => [c, {}]],
        // Translation dictionary
        [
          (c) => Array.isArray(c) && typeof c[0] !== 'string',
          (translationDictionary) => [t(translationDictionary as TranslationDictionary), {}],
        ],
        // MapStrategy
        [
          (c) => Array.isArray(c) && typeof c[0] === 'string',
          (mapStrategy) => resolveMapStrategy(product, mapStrategy as ProductAdornmentDefinitionMapStrategy<any>),
        ],
        // Object
        [(c) => c !== null && typeof c === 'object', (c) => [null, c]],
        // Default
        [() => true, () => [null, {}]],
      ])(content);

      return {
        ...r,
        [adornmentPosition]: [
          ...(r[adornmentPosition] || []),
          <Component key={`${adornmentPosition}${type}`} {...props} {...(moreProps as any)} product={product}>
            {children}
          </Component>,
        ],
      };
    }, {});
  }, [product, productAdornments, t, view]);
}

/**
 * @return Tuple `[children, moreProps]`
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
function resolveMapStrategy<P>(product: P, mapStrategyCfg: ProductAdornmentDefinitionMapStrategy<any>) {
  return [null, {}];
}

function transform<M extends AdornmentMapConfig>(viewMaps: M): AdornmentMap {
  return Object.entries(viewMaps).reduce(
    (nextViewMaps, [view, vmp]) => ({
      ...nextViewMaps,
      [view]: Object.entries(vmp).reduce(
        (viewMap, [adornmentPosition, adornments]) => ({
          ...viewMap,
          ...adornments.reduce((ret, cfg) => {
            const [adornmentType, component, props] = Array.isArray(cfg) ? cfg : [cfg];

            return {
              ...ret,
              [adornmentType]: [adornmentPosition, component || ProductImageDecoratedAdornment, props],
            };
          }, {}),
        }),
        {},
      ),
    }),
    {},
  );
}

type AdornmentMapConfig = {
  [viewName: string]: {
    [adornmentPosition: string]: Array<
      never | string | [type: string, component: null | React.ElementType, prods: Record<string, any>]
    >;
  };
};

type AdornmentMap = {
  [viewName: string]: {
    [type: string]: [adornmentPosition: string, component: React.ElementType, props: Record<string, any>];
  };
};
